import React, { useState, useEffect } from "react";
import { Search } from "@mui/icons-material";
/* MATERIAL COMPONENTS */
import Grid from "@mui/material/Grid";
import {
  Autocomplete,
  Box,
  Card,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

/* HELPERS */
import ChartVar01 from "./charts/ChartVar01";
import ChartAmount02 from "./charts/ChartAmount02";
import ChartRecords03 from "./charts/ChartRecords03";
import DashConsumoContratualDataset from "../../components/data/DashConsumoContratualDataSet";
import DashConsumoContratual from "../../components/classes/DashConsumoContratual";
/*STYLES*/
import "../../assets/css/dashboard_styles.css";
import DashboardConsumoGrid from "./grid";

import ContratoDataset from "../../components/data/ContratoDataSet";
import SkuDataSet from "../../components/data/UnidadeArmazenamentoDataSet";

import { useTranslation } from "react-i18next";

export default function DashboardConsumoCharts() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === "en-US"

  const dashConsumoContratualDataset = new DashConsumoContratualDataset();
  const contratoDataSet = new ContratoDataset();
  const skuDataSet = new SkuDataSet();

  const codigoFornecedorSap = Number(localStorage.getItem("CodigoFornecedorSAPSelecionado"));

  // STATES
  const [filtroNumeroContratoSap, setFiltroNumeroContratoSap] = useState<string[]>([]);
  const [filtroNumeroContratoPortal, setFiltroNumeroContratoPortal] = useState<string[]>([]);
  const [filtroSkuNome, setFiltroSkuNome] = useState<string[]>([]);
  const [filterControl, setFilterControl] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dadosFiltradosArr, setDadosFiltradosArr] = useState<DashConsumoContratual[]>([]);
  const [dadosFiltrados, setDadosFiltrados] = useState<{
    quantidadeProduzida: number;
    quantidadeTotal: number;
    porcentagemProduzida: number;
    porcentagemTempoContrato: number;
    variacaoMinima: number;
    variacaoMaxima: number;
  }>({
    porcentagemProduzida: 0,
    porcentagemTempoContrato: 0,
    quantidadeProduzida: 0,
    quantidadeTotal: 0,
    variacaoMaxima: 0,
    variacaoMinima: 0
  });
  const [numerosContratoSap, setNumerosContratoSap] = useState<string[]>([]);
  const [numerosContratoPortal, setNumerosContratoPortal] = useState<string[]>([]);
  const [skusNomes, setSkusNomes] = useState<string[]>([]);

  useEffect(() => {
    fillInicialData();
  }, [])

  useEffect(() => {
    fillData();
  }, [filterControl]);

  const fillInicialData = async () => {
    try {
      setIsLoading(true);

      var contratosPortal = await contratoDataSet.ListarAtivos(codigoFornecedorSap);
      var contratosSap = await dashConsumoContratualDataset.listarContratosSap(codigoFornecedorSap);
      var skus = await skuDataSet.ListarAtivos();
      setNumerosContratoSap(contratosSap);
      setNumerosContratoPortal(contratosPortal.map(x => x.numeroContrato + ''));
      setSkusNomes(skus.map(x => x.idSKU + ''));

    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }

  const fillData = async () => {
    try {
      var result = await dashConsumoContratualDataset.getAll(codigoFornecedorSap, filtroNumeroContratoSap, filtroNumeroContratoPortal, filtroSkuNome);

      setDadosFiltradosArr(result);
      setDadosFiltrados(realizarOperacoes(result));

    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }

  const realizarOperacoes = (dadosFiltradosArr: DashConsumoContratual[]) => {

    var groupedByItemContrato = dadosFiltradosArr?.reduce(function (r, a) {
      let key = `${a.idSku}-${a.numeroContratoPortal}`;
      r[key] = r[key] || [];
      r[key].push(a);
      return r;
    }, Object.create(null)) ?? [];

    let quantidadeTotal = 0;
    Object.keys(groupedByItemContrato).forEach(key => {
      quantidadeTotal += groupedByItemContrato[key][0].quantidadeTotal;
    });

    let data = {
      quantidadeProduzida: dadosFiltradosArr.reduce((somaParcial, item) => somaParcial + item.quantidadeProduzida, 0),
      quantidadeTotal: quantidadeTotal,
      porcentagemProduzida: dadosFiltradosArr.reduce((somaParcial, item) => somaParcial + item.porcentagemProduzida, 0) / dadosFiltradosArr.length,
      porcentagemTempoContrato: dadosFiltradosArr.reduce((somaParcial, item) => somaParcial + item.porcentagemTempoContrato, 0) / dadosFiltradosArr.length,
      variacaoMinima: 10,
      variacaoMaxima: 10,
    };
    return data;
  }

  return (
    isLoading ?
      <div
        style={{
          display: "flex",
          alignItems: "center",
        }}
        className="dashboard-container">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
          className="dashboard-content">
          <CircularProgress size={48} />
          <small style={{ marginTop: 10 }}>{t("pages.dashboardContractualComsumption.text.loadingData")}</small>
        </div>
      </div>
      :
      <div className="dashboard-container">
        <div className="dashboard-content">
          <Grid
            container
            spacing="16"
            className="dashboard-header"
            sx={{ marginTop: "0" }}
          >
            <Grid item xs={12} lg={3}>
              <Typography variant="subtitle2">{t("pages.dashboardContractualComsumption.text.portalContract")}</Typography>
              <div
                style={{
                  width: "100%",
                  height: "6em",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Autocomplete
                  disablePortal
                  id="skus"
                  options={numerosContratoPortal}
                  value={filtroNumeroContratoPortal}
                  multiple={true}
                  onChange={(e: any, newValue: string[] | null) => {
                    setFiltroNumeroContratoPortal(newValue ?? [""]);
                  }}
                  renderInput={(params) => <TextField {...params} />}
                  style={{ zIndex: 1000 }}
                  sx={{ width: '100%' }}
                />
              </div>
            </Grid>
            <Grid item xs={12} lg={3}>
              <Typography variant="subtitle2">{t("pages.dashboardContractualComsumption.text.sapContract")}</Typography>
              <div
                style={{
                  width: "100%",
                  height: "6em",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Autocomplete
                  disablePortal
                  id="skus"
                  options={numerosContratoSap}
                  value={filtroNumeroContratoSap}
                  multiple={true}
                  onChange={(e: any, newValue: string[] | null) => {
                    setFiltroNumeroContratoSap(newValue ?? [""]);
                  }}
                  renderInput={(params) => <TextField {...params} />}
                  style={{ zIndex: 1000 }}
                  sx={{ width: '100%' }}
                />
              </div>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Typography variant="subtitle2" sx={{ textTransform: 'uppercase' }}>{t("common.sku")}</Typography>
              <div
                style={{
                  width: "100%",
                  height: "6em",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Autocomplete
                  disablePortal
                  id="skus"
                  options={skusNomes}
                  value={filtroSkuNome}
                  multiple={true}
                  onChange={(e: any, newValue: string[] | null) => {
                    setFiltroSkuNome(newValue ?? [""]);
                  }}
                  renderInput={(params) => <TextField {...params} />}
                  style={{ zIndex: 1000 }}
                  sx={{ width: '100%' }}
                />
              </div>
            </Grid>

            <Grid item xs={12} lg={1}>
              <Typography variant="subtitle2" sx={{ textTransform: 'uppercase' }}>{t("common.filter")}</Typography>
              <div
                style={{
                  width: "100%",
                  height: "6em",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Tooltip title={t("pages.dashboardContractualComsumption.text.updateDashboardWithSelectedFilters")}>
                  <span>
                    <IconButton onClick={() => setFilterControl(!filterControl)}>
                      <Search />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </Grid>

          </Grid>


          <Grid
            container
            spacing="16"
            className="dashboard-charts"
            sx={{ marginTop: "0" }}
          >
            <Grid item xs={12} lg={4}>
              <Card className="dashboard-chart__card">
                <Box
                  className="dashboard-flexbox"
                  pt={2}
                  sx={{ justifyContent: "center" }}
                >
                  <Box
                    className="dashboard-box__item"
                    sx={{ minHeight: "78px" }}
                  >
                    {/* <WaterIcon sx={{ marginTop: "8px" }} /> */}
                    <Typography
                      variant="subtitle2"
                      fontSize="small"
                      sx={{ lineHeight: "1.2em", color: "#4c4c4c" }}
                    >
                      {isEnglish ? (
                        <>
                          {t("pages.dashboardContractualComsumption.text.minimal")} <br /> {t("pages.dashboardContractualComsumption.text.variation")}
                        </>
                      ) : (
                        <>
                          {t("pages.dashboardContractualComsumption.text.variation")} <br /> {t("pages.dashboardContractualComsumption.text.minimal")}
                        </>
                      )}
                    </Typography>
                    <div className="h-rule"></div>
                    <Typography variant="subtitle2" pt={1} color="#4c4c4c">
                      {dadosFiltrados.variacaoMinima}%
                    </Typography>
                  </Box>
                  <Box
                    className="dashboard-box__item"
                    sx={{ minHeight: "78px" }}
                  >
                    {/* <AnchorIcon sx={{ marginTop: "8px" }} /> */}
                    <Typography
                      variant="subtitle2"
                      fontSize="small"
                      sx={{ lineHeight: "1.2em", color: "#4c4c4c" }}
                    >
                      {isEnglish ? (
                        <>
                          {t("pages.dashboardContractualComsumption.text.maximum")} <br /> {t("pages.dashboardContractualComsumption.text.variation")}
                        </>
                      ) : (
                        <>
                          {t("pages.dashboardContractualComsumption.text.variation")} <br /> {t("pages.dashboardContractualComsumption.text.maximum")}
                        </>
                      )}
                    </Typography>
                    <div className="h-rule"></div>
                    <Typography variant="subtitle2" pt={1} color="#4c4c4c">
                      {dadosFiltrados.variacaoMaxima}%
                    </Typography>
                  </Box>
                </Box>
              </Card>
              <Card
                className="dashboard-chart__card"
                sx={{ paddingTop: "32px", paddingBottom: "16px" }}
              >
                <div className="chart-box">
                  {t("pages.dashboardContractualComsumption.text.percentageByContractTime")}
                  <ChartVar01
                    porcentagemTempoContrato={
                      dadosFiltrados.porcentagemTempoContrato
                    }
                  />
                </div>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Grid container>
                <Grid item xs={12} sx={{ minHeight: "350px" }}>
                  <Card className="dashboard-chart__card">
                    <Box
                      className="dashboard-flexbox"
                      pt={2}
                      sx={{ justifyContent: "center" }}
                    >
                      <Box
                        className="dashboard-box__item"
                        sx={{ minHeight: "78px" }}
                      >
                        {/* <WaterIcon sx={{ marginTop: "8px" }} /> */}
                        <Typography
                          variant="subtitle2"
                          fontSize="small"
                          sx={{ lineHeight: "1.2em", color: "#4c4c4c" }}
                        >
                          {t("pages.dashboardContractualComsumption.text.volume")} <br /> {t("pages.dashboardContractualComsumption.text.consumed")}
                        </Typography>
                        <div className="h-rule"></div>
                        <Typography variant="subtitle2" pt={1} color="#4c4c4c">
                          {dadosFiltrados.quantidadeProduzida.toString()}
                        </Typography>
                      </Box>

                      <Box
                        className="dashboard-box__item"
                        sx={{ minHeight: "78px" }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontSize="small"
                          sx={{ lineHeight: "1.2em", color: "#4c4c4c" }}
                        >
                          {isEnglish ? (
                            <>
                              {t("pages.dashboardContractualComsumption.text.totalContract")} <br /> {t("pages.dashboardContractualComsumption.text.volume")}
                            </>
                          ) : (
                            <>
                              {t("pages.dashboardContractualComsumption.text.volume")} <br /> {t("pages.dashboardContractualComsumption.text.totalContract")}
                            </>
                          )}
                        </Typography>
                        <div className="h-rule"></div>
                        <Typography variant="subtitle2" pt={1} color="#4c4c4c">
                          {dadosFiltrados.quantidadeTotal.toString()}
                        </Typography>
                      </Box>
                    </Box>

                    <Card
                      className="dashboard-chart__card"
                      sx={{ paddingTop: "32px", paddingBottom: "16px" }}
                    >
                      <div className="chart-box">
                        {t("pages.dashboardContractualComsumption.text.percentageOfQuantityProducedBeforeTheTotalPlan")}
                        <ChartAmount02
                          porcentagemUtilizada={
                            (dadosFiltrados.quantidadeProduzida /
                              dadosFiltrados.quantidadeTotal) *
                            100
                          }
                        />
                      </div>
                    </Card>
                  </Card>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Grid container>
                <Grid item xs={12} sx={{ minHeight: "350px" }}>
                  <Card className="dashboard-chart__card">
                    <div className="chart-box">
                      <ChartRecords03
                        dadosQuantidadeContrato={dadosFiltradosArr}
                      />
                    </div>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <DashboardConsumoGrid
                dashConsumoContratualArr={dadosFiltradosArr}
              />
            </Grid>
          </Grid>
        </div>
      </div>
  );
}

interface DashboardConsumoPropsTypes {
  dashConsumoContratualArr: DashConsumoContratual[];
}

{
  /* <Grid
          container
          spacing="16"
          className="dashboard-header"
          sx={{ marginTop: "0" }}
        >
          <Grid item xs={12} lg={4}>
            <Card className="dashboard-header__card">
              <AutocompleteParceiro />
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card className="dashboard-header__card">
              <AutocompleteSku />
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card className="dashboard-header__card">
              <div className="dashboard-flexbox">
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={ptBR}
                >
                  {/* <DatePicker
                    inputFormat="dd/MM/yyyy"
                    label="Data Inicial"
                    value={valueDTinicio}
                    renderInput={(params) => (
                      <TextField
                        sx={{ maxWidth: "200px", marginRight: "12px" }}
                        {...params}
                      />
                    )}
                    onChange={(newValue) => {
                      setValueDTinicio(newValue);
                    }}
                  /> }
                </LocalizationProvider>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={ptBR}
                >
                  {/* <DatePicker
                    className="datePicker"
                    inputFormat="dd/MM/yyyy"
                    label="Data Final"
                    value={valueDTfim}
                    renderInput={(params) => (
                      <TextField sx={{ maxWidth: "200px" }} {...params} />
                    )}
                    onChange={(newValue) => {
                      setValueDTfim(newValue);
                    }}
                  /> }
                </LocalizationProvider>
              </div>
            </Card>
          </Grid>
        </Grid> */
}
